/**
 * Phase 2L-A79 — Invite-a-neighbour public form.
 *
 * Flow:
 *   1. On mount: GET /api/invite/lookup?ref=<token>
 *      → 200: render the form with referrer first name + remaining count
 *      → 404: render a friendly "link no longer active" message
 *      → 429: render a "too many requests" message
 *   2. On submit: POST /api/invite/send
 *      → 200: thank-you screen showing the voucher codes generated
 *      → 422: surface the `code`/`message` from the validation error
 *      → 429: rate-limited message
 *
 * The token in the URL is the OPAQUE referral_token written by A77 to
 * `review_followups.referral_token`. The page NEVER displays or stores
 * customer email / booking refs — only the referrer's first name.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_ROW = { first_name: "", email: "", suburb: "" };

export default function InviteNeighbour() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";

  const [phase, setPhase] = useState("loading"); // loading | form | thanks | invalid | rate_limited | feature_disabled
  const [lookup, setLookup] = useState(null);
  const [rows, setRows] = useState([{ ...EMPTY_ROW }]);
  const [note, setNote] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    if (!ref) {
      setPhase("invalid");
      return;
    }
    (async () => {
      try {
        const r = await fetch(`${API}/api/invite/lookup?ref=${encodeURIComponent(ref)}`);
        if (!alive) return;
        if (r.status === 429) {
          setPhase("rate_limited");
          return;
        }
        if (r.status === 503) {
          setPhase("feature_disabled");
          return;
        }
        if (!r.ok) {
          setPhase("invalid");
          return;
        }
        const data = await r.json();
        setLookup(data);
        setPhase("form");
      } catch {
        if (alive) setPhase("invalid");
      }
    })();
    return () => { alive = false; };
  }, [ref]);

  const updateRow = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const addRow = () => {
    if (!lookup) return;
    if (rows.length >= lookup.invites_remaining) return;
    setRows((prev) => [...prev, { ...EMPTY_ROW }]);
  };

  const removeRow = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    const cleaned = rows
      .map((r) => ({ first_name: r.first_name.trim(), email: r.email.trim(), suburb: r.suburb.trim() }))
      .filter((r) => r.first_name && r.email);
    if (cleaned.length === 0) {
      setErrorMsg("Add at least one neighbour with their first name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/invite/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ref,
          invites: cleaned,
          personal_note: note.trim() || undefined,
          company, // honeypot — must stay empty for a real user
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok) {
        setResult(body);
        setPhase("thanks");
      } else if (r.status === 429) {
        setErrorMsg(body?.detail?.message ||
          "Too many invite attempts from this device. Please try again in an hour.");
      } else {
        setErrorMsg(body?.detail?.message || "Something went wrong. Please try again.");
      }
    } catch {
      setErrorMsg("Network error — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ─────────────── render states ───────────────

  if (phase === "loading") {
    return (
      <Frame>
        <p data-testid="invite-loading" className="text-slate-600">Loading…</p>
      </Frame>
    );
  }
  if (phase === "invalid") {
    return (
      <Frame>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="invite-invalid-title">
          This invite link is no longer active
        </h1>
        <p className="text-slate-600 leading-relaxed mb-4" data-testid="invite-invalid-body">
          The link may have expired, been fully used, or been disabled. If you believe this is a
          mistake, please reply to your results email or call us on (07) 5327 3473.
        </p>
        <a
          data-testid="invite-invalid-home-link"
          href="/"
          className="inline-block bg-slate-900 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-slate-800 transition"
        >
          Visit Aware Water Testing →
        </a>
      </Frame>
    );
  }
  if (phase === "feature_disabled") {
    return (
      <Frame>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="invite-feature-disabled-title">
          Invite a neighbour — temporarily unavailable
        </h1>
        <p className="text-slate-600 leading-relaxed mb-5" data-testid="invite-feature-disabled-body">
          We&apos;ve briefly paused the Invite-a-Neighbour tool. Your existing voucher
          links remain valid. If you&apos;d like to send a neighbour a $50 first-test
          voucher right now, just reply to your results email with their first name
          and email address and we&apos;ll generate one for you.
        </p>
        <a
          data-testid="invite-feature-disabled-mailto"
          href="mailto:lab@awarewatertesting.com.au?subject=Invite%20a%20neighbour"
          className="inline-block bg-slate-900 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-slate-800 transition"
        >
          Email us your neighbour&apos;s details →
        </a>
      </Frame>
    );
  }
  if (phase === "rate_limited") {
    return (
      <Frame>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="invite-rate-limited">
          Too many requests
        </h1>
        <p className="text-slate-600 leading-relaxed">
          Please wait an hour and try again.
        </p>
      </Frame>
    );
  }
  if (phase === "thanks") {
    return (
      <Frame>
        <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="invite-thanks-title">
          Vouchers on the way
        </h1>
        <p className="text-slate-600 leading-relaxed mb-5">
          We&apos;ve emailed {result?.sent || 0}{" "}
          {result?.sent === 1 ? "neighbour" : "neighbours"} their personal ${lookup.voucher_amount}{" "}
          voucher. They&apos;ll see your first name in the from line.
        </p>
        {(result?.invites || []).length > 0 && (
          <div
            data-testid="invite-thanks-codes"
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6"
          >
            <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-emerald-700 mb-2">
              Voucher codes sent
            </div>
            <ul className="space-y-1">
              {result.invites.map((inv) => (
                <li key={inv.id} className="text-sm text-emerald-900 font-mono">
                  {inv.invitee_first_name} ({inv.invitee_email}) — <strong>{inv.voucher_code}</strong>
                </li>
              ))}
            </ul>
          </div>
        )}
        {result?.failed > 0 && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            {result.failed} email{result.failed === 1 ? "" : "s"} failed to send. The voucher
            codes were still created — reply to your results email and we&apos;ll resend them
            for you.
          </p>
        )}
        <a
          data-testid="invite-thanks-home-link"
          href="/"
          className="inline-block bg-slate-900 text-white px-6 py-3 rounded-full font-semibold text-sm hover:bg-slate-800 transition"
        >
          Back to home →
        </a>
      </Frame>
    );
  }

  // phase === "form"
  const remaining = lookup.invites_remaining;
  const canAddRow = rows.length < remaining;
  return (
    <Frame>
      <h1 className="text-3xl font-bold text-slate-900 leading-tight mb-3" data-testid="invite-form-title">
        Send a neighbour a ${lookup.voucher_amount} first-test voucher
      </h1>
      <p className="text-slate-600 leading-relaxed mb-2" data-testid="invite-form-intro">
        Thanks{lookup.referrer_first_name && `, ${lookup.referrer_first_name}`}. You can invite up to{" "}
        <strong>{lookup.invites_limit}</strong> neighbours, friends or family on tank water.
        We&apos;ll email each of them their own ${lookup.voucher_amount} first-test voucher
        — valid for {lookup.voucher_expiry_days} days. From-line will show your first name so they
        know it&apos;s genuinely from you.
      </p>
      <p className="text-xs text-slate-500 mb-6" data-testid="invite-form-remaining">
        {remaining} invite{remaining === 1 ? "" : "s"} remaining on this link.
      </p>

      <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
        {rows.map((row, idx) => (
          <div
            key={idx}
            data-testid={`invite-row-${idx}`}
            className="bg-slate-50 border border-slate-200 rounded-xl p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] tracking-[0.2em] font-bold uppercase text-slate-500">
                Neighbour {idx + 1}
              </div>
              {rows.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  data-testid={`invite-row-${idx}-remove`}
                  className="text-xs text-slate-500 hover:text-slate-900 underline"
                >
                  remove
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <input
                data-testid={`invite-row-${idx}-first-name`}
                type="text"
                required
                maxLength={80}
                placeholder="First name"
                value={row.first_name}
                onChange={(e) => updateRow(idx, "first_name", e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
              />
              <input
                data-testid={`invite-row-${idx}-email`}
                type="email"
                required
                maxLength={254}
                placeholder="Email"
                value={row.email}
                onChange={(e) => updateRow(idx, "email", e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
              />
              <input
                data-testid={`invite-row-${idx}-suburb`}
                type="text"
                maxLength={80}
                placeholder="Suburb (optional)"
                value={row.suburb}
                onChange={(e) => updateRow(idx, "suburb", e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
              />
            </div>
          </div>
        ))}

        {canAddRow && (
          <button
            type="button"
            onClick={addRow}
            data-testid="invite-add-row"
            className="text-sm font-semibold text-cyan-700 hover:text-cyan-900"
          >
            + Add another neighbour
          </button>
        )}

        <div>
          <label className="text-[10px] tracking-[0.2em] font-bold uppercase text-slate-500 block mb-2">
            Personal note (optional)
          </label>
          <textarea
            data-testid="invite-personal-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="I thought this might be helpful for you because you're on tank water too."
            className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 bg-white"
          />
        </div>

        {/* Honeypot — must remain empty. Visually hidden but reachable
            by naive form-fillers. Real users never touch it. */}
        <input
          type="text"
          name="company"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          data-testid="invite-honeypot"
          style={{ position: "absolute", left: "-9999px", top: "-9999px", height: 0, width: 0, opacity: 0 }}
          aria-hidden="true"
        />

        {errorMsg && (
          <div
            data-testid="invite-form-error"
            className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-4 py-3"
          >
            {errorMsg}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="invite-submit"
          className="w-full md:w-auto bg-gradient-to-br from-cyan-500 to-sky-600 text-white px-8 py-3 rounded-full font-semibold text-sm hover:opacity-90 transition disabled:opacity-50"
        >
          {submitting ? "Sending…" : `Send ${rows.length} invite${rows.length === 1 ? "" : "s"}`}
        </button>
        <p className="text-[11px] text-slate-500 mt-3">
          Voucher applies to first Aware water test booking only. One voucher per household.
          Cannot be combined with other offers unless approved by Aware. Expiry applies.
        </p>
      </form>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div
      data-testid="invite-neighbour-page"
      className="min-h-screen bg-slate-50 px-4 py-16"
    >
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-10">
        <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-cyan-600 mb-2">
          Aware Water Testing
        </div>
        {children}
        <p className="text-xs text-slate-400 mt-8">
          Independent water testing laboratory · Noosaville QLD · (07) 5327 3473
        </p>
      </div>
    </div>
  );
}
